import React, {
  createContext,
  PropsWithChildren,
  useState,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { getStockSummary, getStockDetails } from '../../../stock';
import { useReportingFilters } from './ReportingFiltersContext';
import type { ApiRecord } from './types';

export interface StockDetailEntry {
  blockeds: ApiRecord[];
  voyages: ApiRecord[];
}

export type StockDetailCache = Record<string, StockDetailEntry>;

export interface ReportingStockDataContextValue {
  stockData: ApiRecord[];
  baseStockData: ApiRecord[];
  isStockLoading: boolean;
  fetchStockData: () => Promise<void>;
  fetchStockDetails: () => Promise<StockDetailCache>;
}

const ReportingStockDataContext =
  createContext<ReportingStockDataContextValue | null>(null);

export const ReportingStockDataProvider = ({ children }: PropsWithChildren) => {
  const { buildParams, activeScenario, showError } = useReportingFilters();

  const [isStockLoading, setIsStockLoading] = useState(false);
  const [stockData, setStockData] = useState<ApiRecord[]>([]);
  const [baseStockData, setBaseStockData] = useState<ApiRecord[]>([]);
  const stockDetailCache = useRef<StockDetailCache>({});
  const stockDetailsFetched = useRef(false);

  const fetchStockData = useCallback(async () => {
    setIsStockLoading(true);
    try {
      const params = buildParams();
      const scenarioFetch = getStockSummary(params);

      // When scenario is active, also fetch base stock (without scenarioId) for comparison
      let baseFetch: Promise<ApiRecord[]> | undefined;
      if (activeScenario) {
        const baseParams = new URLSearchParams(params);
        baseParams.delete('scenarioId');
        baseFetch = getStockSummary(baseParams).catch(() => []);
      }

      const [response, baseResponse] = await Promise.all([
        scenarioFetch,
        baseFetch ?? Promise.resolve([]),
      ]);

      setStockData(response || []);
      setBaseStockData(activeScenario ? baseResponse || [] : []);
      // Clear detail cache and fetched flag on new fetch (filters changed)
      stockDetailCache.current = {};
      stockDetailsFetched.current = false;
    } catch (error) {
      showError(error, 'fetchStockData');
      setStockData([]);
      setBaseStockData([]);
    } finally {
      setIsStockLoading(false);
    }
  }, [buildParams, activeScenario, showError]);

  const fetchStockDetails = useCallback(async () => {
    // Already fetched for this filter set — cache is valid
    if (stockDetailsFetched.current) return stockDetailCache.current;

    try {
      const params = buildParams();
      const response = await getStockDetails(params);

      // Merge into cache
      const blockeds: Record<string, ApiRecord[]> = response?.blockeds || {};
      const voyages: Record<string, ApiRecord[]> = response?.voyages || {};
      const allDates = new Set([
        ...Object.keys(blockeds),
        ...Object.keys(voyages),
      ]);
      allDates.forEach((date) => {
        stockDetailCache.current[date] = {
          blockeds: blockeds[date] || [],
          voyages: voyages[date] || [],
        };
      });
      // Mark as fetched even if response was empty — avoids infinite retries
      stockDetailsFetched.current = true;
    } catch (error) {
      // Leave fetched=false on error so next filter change can retry
    }
    return stockDetailCache.current;
  }, [buildParams]);

  return (
    <ReportingStockDataContext.Provider
      value={{
        stockData,
        baseStockData,
        isStockLoading,
        fetchStockData,
        fetchStockDetails,
      }}
    >
      {children}
    </ReportingStockDataContext.Provider>
  );
};

export const useReportingStockData = (): ReportingStockDataContextValue => {
  const ctx = useContext(ReportingStockDataContext);
  if (ctx === null) {
    throw new Error(
      'useReportingStockData must be used within a <ReportingStockDataProvider>'
    );
  }
  return ctx;
};
